/**
 * The earliest hunt start date the archive service has anything for.
 *
 * The archive is built by TracePack, which went live around 15–25 May 2026.
 * Hunts from October 2025 through March 2026 answer 404; hunts from May 2026
 * onward load. Nothing older than this can ever be replayed, whoever asks.
 *
 * **A placeholder until the platform team names the exact day.** It errs late
 * rather than early on purpose: a date set too early shows the button on a
 * batch of hunts that all answer "not recorded", which is the complaint this
 * gate exists to prevent, reintroduced at a different date.
 *
 * It replaced an earlier 9 September 2026 constant that meant something else —
 * the day `joinWithCode` started writing code-joiners into the `hunters`
 * roster. As a gate that threw away every working replay between May and
 * September: roughly 39,700 ended hunts, 24,700 of them in the three weeks
 * before it. The roster check below decides who may replay; the date only has
 * to answer whether an archive exists at all.
 *
 * Compared in UTC, which puts the boundary a few hours from what a Nordic user
 * reads off their own clock: a hunt starting 25 May at 01:00 CEST is 24 May
 * here and stays hidden. The archive's boundary is not local to anyone, so
 * comparing locally would only move the surprise to a different set of users.
 *
 * Mobile carries the same constant by hand in
 * `domains/clubhouse/wehunt-mobile/lib/src/ended_hunt_report/replay_entry_visibility.dart`
 * — Dart and TypeScript in two packages, with nothing that can hold them in
 * step. Change both together, or the two clients offer different hunts.
 */
export const REPLAY_EARLIEST_ARCHIVE = Date.UTC(2026, 4, 25)

export interface ReplayEntryVisibilityInput {
  /** The hunt's start, ISO 8601. */
  huntStartTime: string | null
  /** The hunt's attendance roster — who actually carried a tracker. */
  attendanceUserIds: readonly string[]
  /** The signed-in user, or null while that is still resolving. */
  currentUserId: string | null | undefined
}

/**
 * Whether the ended-hunt report's replay entry can actually deliver a replay.
 *
 * False removes the button entirely rather than disabling it. The product call,
 * settled on mobile, is that a hunt which can never be replayed should not
 * advertise the action at all — a greyed control invites the click it then
 * refuses.
 *
 * Two ways this answers false, and today both end in the same experience: a
 * request the archive service refuses or cannot fill, after a wait.
 *
 * - **The hunt started before any archive existed.** A missing or unreadable
 *   start date counts as predating it: hunt documents with no dates are a
 *   legacy shape, and legacy is exactly the population with no archive.
 * - **This user is not on the attendance roster.** The archive service
 *   authorises against `hunters`, not against the plan's invite list or the
 *   team, so a team member who never hunted is refused — and so is a spectator.
 *
 * The roster is deliberately the only test of WHO. A leader, an invitee who
 * accepted, and a non-invited code-joiner are all on it and all three can
 * replay; narrowing this to leaders would read the same roster and simply keep
 * fewer of them.
 *
 * `currentUserId` is null while the report is still loading. That is not
 * evidence of anything, so the entry stays for that moment rather than popping
 * into existence once the read lands.
 */
export function replayEntryVisible({
  huntStartTime,
  attendanceUserIds,
  currentUserId,
}: ReplayEntryVisibilityInput): boolean {
  if (huntStartTime === null) return false

  const startedAt = Date.parse(huntStartTime)
  if (!Number.isFinite(startedAt)) return false
  if (startedAt < REPLAY_EARLIEST_ARCHIVE) return false

  if (currentUserId === null || currentUserId === undefined || currentUserId === '') return true
  return attendanceUserIds.includes(currentUserId)
}
